import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { chatbotResponses } from '../../data/mock';
import { auditAPI } from '../../services/api';
import { toast } from 'sonner';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '' });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hi! I'm the PixZent assistant. How can I help you today?",
      options: [
        'What services does PixZent offer?',
        'How much does it cost?',
        'How do I get started?',
        'What is AI Visibility?'
      ]
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleOptionClick = (option) => {
    // Add user message
    setMessages(prev => [...prev, { type: 'user', text: option }]);
    
    // Show typing indicator
    setIsTyping(true);
    
    // Simulate response delay
    setTimeout(() => {
      setIsTyping(false);
      const response = chatbotResponses[option] || chatbotResponses['default'];
      
      // Get next options based on previous selections
      const allOptions = Object.keys(chatbotResponses).filter(key => key !== 'default');
      const usedOptions = messages.filter(m => m.type === 'user').map(m => m.text);
      const availableOptions = allOptions.filter(opt => !usedOptions.includes(opt) && opt !== option);
      const nextOptions = availableOptions.slice(0, 4);
      
      // After 2 questions, prompt for lead capture
      const questionsAsked = messages.filter(m => m.type === 'user').length + 1;
      const shouldShowLeadPrompt = questionsAsked >= 2 && !showLeadForm;
      
      setMessages(prev => [
        ...prev,
        {
          type: 'bot',
          text: response,
          options: nextOptions.length > 0 && !shouldShowLeadPrompt ? nextOptions : undefined,
          showLeadCapture: shouldShowLeadPrompt
        }
      ]);
      
      if (shouldShowLeadPrompt) {
        setShowLeadForm(true);
      }
    }, 1000);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingLead(true);
    
    try {
      await auditAPI.createSubmission({
        fullName: leadData.name,
        businessName: leadData.name,
        websiteUrl: 'Via Chatbot',
        email: leadData.email,
        phone: leadData.phone,
        location: 'Not specified',
        industry: 'Not specified',
        challenge: 'Lead from chatbot'
      }, 'Chatbot');
      
      toast.success('Thank you! We\'ll be in touch soon.');
      
      setMessages(prev => [
        ...prev,
        { type: 'user', text: `${leadData.name} - ${leadData.email}` },
        {
          type: 'bot',
          text: `Thank you, ${leadData.name}! Your information has been saved. Our team will contact you shortly to discuss how we can help boost your AI visibility. In the meantime, feel free to explore our Free Audit or Book a Call options!`,
          options: undefined
        }
      ]);
      
      setShowLeadForm(false);
      setLeadData({ name: '', email: '', phone: '' });
    } catch (error) {
      console.error('Failed to submit lead:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const resetChat = () => {
    setMessages([
      {
        type: 'bot',
        text: "Hi! I'm the PixZent assistant. How can I help you today?",
        options: [
          'What services does PixZent offer?',
          'How much does it cost?',
          'How do I get started?',
          'What is AI Visibility?'
        ]
      }
    ]);
    setShowLeadForm(false);
    setLeadData({ name: '', email: '', phone: '' });
  };

  return (
    <>
      {/* Chat Button */}
      <div
        className={`fixed bottom-6 right-6 z-50 group flex items-center justify-end transition-all duration-300 ${
          isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'scale-100 opacity-100'
        }`}
      >
        {/* Label: always visible on mobile, slides in on hover for desktop */}
        <span className="
          mr-3 bg-slate-900/90 backdrop-blur-sm text-white text-xs font-semibold
          px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap pointer-events-none
          transition-all duration-200 ease-out
          opacity-0 translate-x-2
          md:group-hover:opacity-100 md:group-hover:translate-x-0
        ">
          Chat Now
        </span>

        <button
          onClick={() => setIsOpen(true)}
          className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 via-violet-600 to-pink-500 text-white shadow-2xl shadow-violet-500/30 hover:shadow-violet-500/50 hover:scale-110 transition-all duration-300 flex-shrink-0"
          aria-label="Open chat"
        >
          <MessageCircle className="w-7 h-7" />
          <span className="absolute inset-0 rounded-full bg-violet-500 animate-ping opacity-20 pointer-events-none" />
        </button>
      </div>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-3xl shadow-2xl shadow-slate-900/20 border border-slate-200 overflow-hidden transition-all duration-500 ${
          isOpen
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-8 scale-95 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">PixZent Assistant</h3>
              <p className="text-white/70 text-xs">Usually replies instantly</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-slate-50">
          {messages.map((message, index) => (
            <div key={index}>
              <div
                className={`flex items-start gap-3 ${
                  message.type === 'user' ? 'flex-row-reverse' : ''
                }`}
              >
                {/* Avatar */}
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.type === 'user'
                      ? 'bg-gradient-to-br from-blue-500 to-violet-500'
                      : 'bg-slate-200'
                  }`}
                >
                  {message.type === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-slate-600" />
                  )}
                </div>

                {/* Message Bubble */}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white rounded-tr-none'
                      : 'bg-white text-slate-700 shadow-sm rounded-tl-none'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.text}</p>
                </div>
              </div>

              {/* Options */}
              {message.type === 'bot' && message.options && index === messages.length - 1 && !isTyping && (
                <div className="mt-3 ml-11 space-y-2">
                  {message.options.map((option, optIndex) => (
                    <button
                      key={optIndex}
                      onClick={() => handleOptionClick(option)}
                      className="block w-full text-left px-4 py-2.5 bg-white border border-violet-200 text-violet-700 text-sm rounded-xl hover:bg-violet-50 hover:border-violet-300 transition-all duration-200"
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}

              {/* Lead Capture Form */}
              {message.type === 'bot' && message.showLeadCapture && index === messages.length - 1 && !isTyping && (
                <div className="mt-4 ml-11 bg-gradient-to-br from-violet-50 to-pink-50 rounded-xl p-4 border border-violet-200">
                  <p className="text-sm font-medium text-slate-700 mb-3">
                    Want us to help you get started? Leave your details:
                  </p>
                  <form onSubmit={handleLeadSubmit} className="space-y-2">
                    <Input
                      placeholder="Your name *"
                      value={leadData.name}
                      onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                      required
                      className="h-9 text-sm rounded-lg"
                    />
                    <Input
                      type="email"
                      placeholder="Your email *"
                      value={leadData.email}
                      onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                      required
                      className="h-9 text-sm rounded-lg"
                    />
                    <Input
                      type="tel"
                      placeholder="Your phone *"
                      value={leadData.phone}
                      onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                      required
                      className="h-9 text-sm rounded-lg"
                    />
                    <Button
                      type="submit"
                      disabled={isSubmittingLead}
                      className="w-full h-9 text-sm bg-gradient-to-r from-blue-600 to-violet-600 text-white rounded-lg"
                    >
                      {isSubmittingLead ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Get in Touch'
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                <Bot className="w-4 h-4 text-slate-600" />
              </div>
              <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Footer */}
        <div className="p-4 bg-white border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={resetChat}
              className="text-xs border-slate-200 text-slate-500 hover:text-violet-600 hover:border-violet-200"
            >
              Reset Chat
            </Button>
            <a
              href="#audit"
              onClick={() => setIsOpen(false)}
              className="flex-1 flex items-center justify-center gap-2 h-9 bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              Get Free Audit
              <Send className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
